import { useEffect, useMemo, useState } from 'preact/hooks';
import { useIsWide } from '../lib/useIsWide';
import { dismissalKey } from '../lib/dismissals';
import { state, slug, archiveRecipe, restoreRecipe, addMealToSlot } from '../store/store';
import { showToast } from '../store/toast';
import { Layout } from '../components/Layout';
import { RecipeCard } from '../components/RecipeCard';
import { suggestForSlot } from '../lib/suggest';
import { daysSince, todayIso, weekIdOf } from '../lib/dates';
import { navigate } from '../router';
import { AddToPlanModal } from '../components/AddToPlanModal';
import type { Cuisine, MealType, Recipe, Status } from '../types';

type SortKey = 'recommended' | 'recent' | 'name' | 'never';
type StatusFilter = 'all' | NonNullable<Status> | 'untagged';

const CATEGORIES: { key: NonNullable<Recipe['category']> | 'all'; label: string }[] = [
  { key: 'all', label: 'All categories' },
  { key: 'sandwich', label: 'Sandwich' },
  { key: 'pinwheel', label: 'Pinwheel' },
  { key: 'quesadilla', label: 'Quesadilla' },
  { key: 'bento', label: 'Bento' },
  { key: 'thermos', label: 'Thermos' },
  { key: 'pasta', label: 'Pasta' },
  { key: 'rice-grain', label: 'Rice / Grain' },
  { key: 'mini-muffin', label: 'Mini muffin' },
  { key: 'breakfast-for-lunch', label: 'Breakfast-for-lunch' },
  { key: 'dippable', label: 'Dippable' },
  { key: 'hidden-veggie', label: 'Hidden veggie' },
];


export function RecipesPage(props: {
  slot?: { date: string; meal_type: MealType };
}) {
  const s = state.value!;
  const allRecipes = s.recipes;
  const isSlotPicker = !!props.slot;

  const isColdStart = useMemo(
    () => allRecipes.every(r => r.status === null) && s.weeks.every(w => w.meals.length === 0),
    [allRecipes, s.weeks],
  );

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | NonNullable<Recipe['category']>>('all');
  const [mealFilter, setMealFilter] = useState<'all' | 'lunch' | 'dinner'>('all');
  const [cuisineFilter, setCuisineFilter] = useState<'all' | Cuisine>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState<SortKey>(isColdStart ? 'recommended' : 'recent');
  const [planRecipeId, setPlanRecipeId] = useState<string | null>(null);
  const [introDismissed, setIntroDismissed] = useState(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem(dismissalKey(slug.value, 'recipes-intro')) === '1'
  );

  // If isColdStart flips false later (after first tag), don't override the user.
  // The initial useState runs once.
  useEffect(() => { /* no-op; default sort decided at mount */ }, []);

  function setMeal(next: typeof mealFilter) {
    setMealFilter(next);
    // Clear filters that disappear from the UI when switching meal type.
    if (next === 'dinner') setCategoryFilter('all');
    if (next === 'lunch') setCuisineFilter('all');
    // 'all' shows both dropdowns — keep current values.
  }

  function dismissIntro() {
    setIntroDismissed(true);
    try { localStorage.setItem(dismissalKey(slug.value, 'recipes-intro'), '1'); } catch {}
  }

  const lastSeenByRecipe = useMemo(() => {
    const map = new Map<string, { rating: 'up' | 'meh' | 'down' | null; daysAgo: number | null; date: string | null }>();
    const today = new Date().toISOString().slice(0, 10);
    for (const w of s.weeks) for (const m of w.meals) {
      if (!m.recipe_id) continue;
      const prev = map.get(m.recipe_id);
      if (!prev || (m.date > (prev.date ?? ''))) {
        map.set(m.recipe_id, {
          rating: m.rating, daysAgo: daysSince(m.date, today), date: m.date,
        });
      }
    }
    return map;
  }, [s]);

  // Pool after status/meal/category/cuisine/search but BEFORE source filter —
  // used both as input to the source dropdown (for distinct sources + counts)
  // and as the base for the final visible list.
  const preSource = useMemo(() => {
    let list = allRecipes;
    // Archived: hide by default; the slot-picker always hides them.
    if (props.slot || !showArchived) list = list.filter(r => !r.archived);
    if (props.slot) {
      const mt = props.slot.meal_type;
      list = list.filter(r => r.meal_types.includes(mt) && r.status !== 'no');
    }
    if (statusFilter !== 'all') {
      list = list.filter(r => statusFilter === 'untagged' ? r.status === null : r.status === statusFilter);
    }
    if (categoryFilter !== 'all') list = list.filter(r => r.category === categoryFilter);
    if (mealFilter !== 'all') list = list.filter(r => r.meal_types.includes(mealFilter));
    if (cuisineFilter !== 'all') list = list.filter(r => r.cuisine === cuisineFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
    }
    return list;
  }, [allRecipes, statusFilter, categoryFilter, mealFilter, cuisineFilter, search, showArchived, props.slot]);

  const sourceOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of preSource) {
      if (r.source_name) counts.set(r.source_name, (counts.get(r.source_name) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [preSource]);

  const visible = useMemo(() => {
    if (sourceFilter === 'all') return preSource;
    return preSource.filter(r => r.source_name === sourceFilter);
  }, [preSource, sourceFilter]);

  const archivedCount = useMemo(
    () => allRecipes.filter(r => r.archived).length,
    [allRecipes],
  );

  // Set of recipe ids that are scheduled in the current week — used to highlight
  // cards that are already on the plan.
  const currentWeekRecipeIds = useMemo(() => {
    const wid = weekIdOf(todayIso());
    const week = s.weeks.find(w => w.id === wid);
    if (!week) return new Set<string>();
    return new Set(week.meals.map(m => m.recipe_id).filter((id): id is string => !!id));
  }, [s.weeks]);

  const sorted = useMemo(() => {
    const list = [...visible];
    // Photos-first is the primary sort across all modes — recipes with hero
    // photos are easier to evaluate at a glance, so they always lead.
    const photoRank = (r: Recipe) => (r.image_url ? 0 : 1);
    if (sort === 'name') {
      list.sort((a, b) => photoRank(a) - photoRank(b) || a.name.localeCompare(b.name));
    } else if (sort === 'never') {
      list.sort((a, b) =>
        photoRank(a) - photoRank(b) ||
        Number(lastSeenByRecipe.has(a.id)) - Number(lastSeenByRecipe.has(b.id)) ||
        a.name.localeCompare(b.name)
      );
    } else if (sort === 'recent') {
      list.sort((a, b) => {
        const pr = photoRank(a) - photoRank(b);
        if (pr !== 0) return pr;
        const ad = lastSeenByRecipe.get(a.id)?.date ?? '';
        const bd = lastSeenByRecipe.get(b.id)?.date ?? '';
        return bd.localeCompare(ad) || a.name.localeCompare(b.name);
      });
    } else if (sort === 'recommended') {
      list.sort((a, b) => photoRank(a) - photoRank(b) || a.name.localeCompare(b.name));
    }
    return list;
  }, [visible, sort, lastSeenByRecipe]);

  const statusCounts = useMemo(() => {
    const pool = showArchived ? allRecipes : allRecipes.filter(r => !r.archived);
    return {
      all: pool.length,
      yes: pool.filter(r => r.status === 'yes').length,
      maybe: pool.filter(r => r.status === 'maybe').length,
      untagged: pool.filter(r => r.status === null).length,
    };
  }, [allRecipes, showArchived]);

  const STATUS_OPTS: { key: StatusFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: statusCounts.all },
    { key: 'yes', label: 'Yes', count: statusCounts.yes },
    { key: 'maybe', label: 'Maybe', count: statusCounts.maybe },
    { key: 'untagged', label: 'Not picked', count: statusCounts.untagged },
  ];

  function openRecipe(r: Recipe) {
    navigate(`/h/${slug.value}/recipes/${r.id}${isSlotPicker ? `?slot=${props.slot!.date}:${props.slot!.meal_type}` : ''}`);
  }

  async function pickForSlot(r: Recipe) {
    if (!props.slot) return;
    const slotDate = props.slot.date;
    const slotMt = props.slot.meal_type;
    await addMealToSlot(weekIdOfSlot(slotDate), slotDate, slotMt, r.id);
    navigate(`/h/${slug.value}/plan/${weekIdOfSlot(slotDate)}`);
  }

  function weekIdOfSlot(d: string): string {
    const date = new Date(d + 'T00:00:00Z');
    const dow = date.getUTCDay();
    const offset = dow === 0 ? -6 : 1 - dow;
    date.setUTCDate(date.getUTCDate() + offset);
    return date.toISOString().slice(0, 10);
  }

  const suggestions = props.slot ? suggestForSlot(s, props.slot) : null;
  const isWide = useIsWide();

  const advancedActiveCount =
    (mealFilter === 'dinner'
      ? (cuisineFilter !== 'all' ? 1 : 0)
      : (categoryFilter !== 'all' ? 1 : 0))
    + (sourceFilter !== 'all' ? 1 : 0)
    + (showArchived ? 1 : 0);

  const [moreOpen, setMoreOpen] = useState(() => advancedActiveCount > 0);

  return (
    <Layout active="recipes" slug={slug.value} rightActions={
      <button onClick={() => navigate(`/h/${slug.value}/recipes/new`)} style={{
        padding: '7px 14px', fontSize: 13, borderRadius: 8, border: 'none',
        background: 'var(--pink-deep)', color: 'white', fontWeight: 600,
      }}>+ New recipe</button>
    }>
      <div style={{ padding: isWide ? '20px 24px' : '12px 14px' }}>
        {props.slot && (
          <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--plum-soft)' }}>
            Picking for <strong>{props.slot.date}</strong> · <strong>{props.slot.meal_type}</strong>
          </div>
        )}

        {/* Cold-start intro */}
        {!isSlotPicker && !introDismissed && isColdStart && (
          <div style={{
            background: 'var(--pink-tint)', border: '1px solid var(--pink-soft-bg)',
            borderRadius: 'var(--radius-lg)', padding: 'var(--space-md)',
            marginBottom: 'var(--space-md)',
            display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)',
            boxShadow: 'var(--shadow-soft)',
          }}>
            <div style={{ flex: 1 }}>
              <strong style={{ display: 'block', marginBottom: 4 }}>Welcome 🍽️</strong>
              <div style={{ fontSize: 13, color: 'var(--plum-soft)', lineHeight: 1.45 }}>
                Tap into a recipe to mark it <strong>Yes</strong> or <strong>Maybe</strong>. Then head to <strong>Plan</strong> to compose your week — the shopping list builds itself.
              </div>
            </div>
            <button onClick={dismissIntro} style={{
              background: 'var(--pink-deep)', color: 'white', border: 'none',
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            }}>Got it</button>
          </div>
        )}

        {/* Compact filter strip */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          flexWrap: 'wrap',
        }}>
          {/* Search: icon expands inline */}
          {searchOpen ? (
            <>
              <input
                autoFocus
                value={search}
                onInput={e => setSearch((e.target as HTMLInputElement).value)}
                onBlur={() => { if (!search) setSearchOpen(false); }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearch('');
                    setSearchOpen(false);
                    (e.currentTarget as HTMLInputElement).blur();
                  }
                }}
                placeholder="Search recipes…"
                style={{
                  flex: '1 1 200px', padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--hairline)', background: 'white', fontSize: 14,
                  color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.4,
                }}
              />
              {search && (
                <button
                  onClick={() => { setSearch(''); setSearchOpen(false); }}
                  aria-label="Clear search"
                  style={{
                    width: 32, height: 32, borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--hairline)', background: 'white',
                    color: 'var(--plum-soft)', fontSize: 16, padding: 0,
                    cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >×</button>
              )}
            </>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              style={{
                width: 44, height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
                background: 'white', fontSize: 14, padding: 0, cursor: 'pointer',
              }}
            >🔍</button>
          )}

          {/* Status segmented control */}
          <div style={{
            display: 'flex', borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
            overflow: 'hidden', background: 'white', height: 44,
          }}>
            {STATUS_OPTS.map((o, i) => {
              const active = o.key === statusFilter;
              return (
                <button
                  key={o.key}
                  onClick={() => setStatusFilter(o.key)}
                  aria-pressed={active}
                  style={{
                    padding: '10px 14px', fontSize: 12, fontWeight: 600,
                    background: active ? 'var(--pink-primary)' : 'white',
                    color: active ? 'var(--on-primary)' : 'var(--ink)',
                    border: 'none',
                    borderLeft: i === 0 ? 'none' : '1px solid var(--hairline)',
                    cursor: 'pointer',
                  }}
                >
                  {o.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>{o.count}</span>
                </button>
              );
            })}
          </div>

          {/* Meal-type segmented control */}
          <div style={{
            display: 'flex', borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
            overflow: 'hidden', background: 'white', height: 44,
          }}>
            {(['all', 'lunch', 'dinner'] as const).map((k, i) => {
              const active = mealFilter === k;
              return (
                <button
                  key={k}
                  onClick={() => setMeal(k)}
                  aria-pressed={active}
                  style={{
                    padding: '10px 14px', fontSize: 12, fontWeight: 600,
                    background: active ? 'var(--pink-primary)' : 'white',
                    color: active ? 'var(--on-primary)' : 'var(--ink)',
                    border: 'none',
                    borderLeft: i === 0 ? 'none' : '1px solid var(--hairline)',
                    cursor: 'pointer',
                  }}
                >
                  {k === 'all' ? 'All' : k === 'lunch' ? 'Lunch' : 'Dinner'}
                </button>
              );
            })}
          </div>

          {/* More filters disclosure */}
          <button
            type="button"
            className="more-filters-toggle"
            aria-expanded={moreOpen}
            aria-controls="recipes-more-filters"
            onClick={() => setMoreOpen(v => !v)}
          >
            More filters
            {advancedActiveCount > 0 && (
              <span className="more-filters-count-badge">{advancedActiveCount}</span>
            )}
          </button>

          {/* Sort dropdown */}
          <select
            value={sort}
            onChange={e => setSort((e.target as HTMLSelectElement).value as SortKey)}
            style={{
              padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
              background: 'white', fontSize: 14, color: 'var(--ink)', marginLeft: 'auto',
              fontFamily: 'inherit', lineHeight: 1.4,
            }}
          >
            <option value="recommended">Sort: Recommended</option>
            <option value="recent">Sort: Recently used</option>
            <option value="never">Sort: Never tried first</option>
            <option value="name">Sort: Alphabetical</option>
          </select>
        </div>

        {/* Second row: advanced filters (collapsible) */}
        <div
          id="recipes-more-filters"
          className={`more-filters${moreOpen ? ' open' : ''}`}
          aria-hidden={!moreOpen}
        >
          {/* Cuisine — relevant for dinners and 'all'; hidden when filter is lunch since lunches have no cuisine. */}
          {mealFilter !== 'lunch' && (
            <select
              value={cuisineFilter}
              onChange={e => setCuisineFilter((e.target as HTMLSelectElement).value as 'all' | Cuisine)}
              style={{
                padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
                background: 'white', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.4,
              }}
            >
              <option value="all">All cuisines</option>
              <option value="vietnamese">Vietnamese</option>
              <option value="chinese">Chinese</option>
              <option value="korean">Korean</option>
              <option value="japanese">Japanese</option>
              <option value="thai">Thai</option>
              <option value="american">American</option>
              <option value="other">Other</option>
            </select>
          )}
          {/* Category — relevant for lunches and 'all'; hidden for dinners since dinners have no category. */}
          {mealFilter !== 'dinner' && (
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter((e.target as HTMLSelectElement).value as any)}
              style={{
                padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
                background: 'white', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.4,
              }}
            >
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          )}

          {/* Source dropdown */}
          <select
            value={sourceFilter}
            onChange={e => setSourceFilter((e.target as HTMLSelectElement).value)}
            style={{
              padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
              background: 'white', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.4,
            }}
          >
            <option value="all">All sources ({preSource.length})</option>
            {sourceOptions.map(s => (
              <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
            ))}
          </select>

          {/* Show archived toggle — only render if there's anything archived */}
          {!isSlotPicker && archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(v => !v)}
              aria-pressed={showArchived}
              style={{
                padding: '12px 14px', height: 44, borderRadius: 'var(--radius-md)',
                border: '1px solid var(--hairline)',
                background: showArchived ? 'var(--pink-primary)' : 'white',
                color: showArchived ? 'var(--on-primary)' : 'var(--ink)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
            </button>
          )}
        </div>
        </div>

        {suggestions && (
          <div style={{ marginBottom: 'var(--space-md)' }}>
            <SuggestionRow label="He liked these recently" recipes={suggestions.likedRecent.map(x => x.r)} onPick={pickForSlot} />
            <SuggestionRow label="Haven't packed in 3+ weeks" recipes={suggestions.variety.map(x => x.r)} onPick={pickForSlot} />
            <SuggestionRow label="Shares ingredients with this week" recipes={suggestions.overlap.map(x => x.r)} onPick={pickForSlot} />
          </div>
        )}

        <div className="recipe-grid">
          {sorted.map(r => (
            <RecipeCard
              key={r.id}
              recipe={r}
              lastSeen={lastSeenByRecipe.get(r.id)}
              onClick={() => isSlotPicker ? pickForSlot(r) : openRecipe(r)}
              onArchive={isSlotPicker ? undefined : async () => { await archiveRecipe(r.id); showToast(`Archived "${r.name}"`, { label: 'Undo', onClick: () => { void restoreRecipe(r.id); } }); }}
              onRestore={isSlotPicker ? undefined : () => { void restoreRecipe(r.id); }}
              onAddToPlan={isSlotPicker ? undefined : (id) => setPlanRecipeId(id)}
              onCurrentWeek={currentWeekRecipeIds.has(r.id)}
            />
          ))}
          {sorted.length === 0 && (
            <div style={{ color: 'var(--plum-soft)', gridColumn: '1 / -1' }}>No recipes match.</div>
          )}
        </div>
        {planRecipeId && (
          <AddToPlanModal
            recipeId={planRecipeId}
            open={true}
            initialWeekId={weekIdOf(todayIso())}
            onClose={() => setPlanRecipeId(null)}
          />
        )}
      </div>
    </Layout>
  );
}

function SuggestionRow(props: { label: string; recipes: Recipe[]; onPick: (r: Recipe) => void }) {
  if (props.recipes.length === 0) return null;
  return (
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <div className="label-caps" style={{ marginBottom: 6 }}>{props.label}</div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
        {props.recipes.slice(0, 6).map(r => (
          <button key={r.id} onClick={() => props.onPick(r)} style={{
            flex: '0 0 auto', minWidth: 180, textAlign: 'left',
            padding: 10, background: 'white', borderRadius: 10,
            border: '1px solid var(--pink-soft)', fontSize: 13, fontWeight: 600,
          }}>{r.name}</button>
        ))}
      </div>
    </div>
  );
}
